import React from 'react'
import { graphql } from 'gatsby'
import { v4 } from 'uuid'
import { Link, useTranslation } from 'gatsby-plugin-react-i18next'
import { Box, Button, HStack, Stack } from '@chakra-ui/react'

import Layout from '../components/layout'
import Rooms from '../components/rooms'
import Features from '../components/features'
import Services from '../components/services'
import ButtonBook from '../components/buttonBook'

import {
  Article,
  Section,
  HeroBody,
  HeroTitle,
  HeroSubtitle,
  HeroCta,
  RowContent,
  ColImage,
  ColInfo,
  P,
  FeaturedBanner,
  Carousel,
  Gallery,
  TestimonialItem,
  Testimonials,
  Image,
  HeroGallery,
  RoomList,
  ColVideo,
} from '../ui'

// import FoodCategories from '../components/foodCategories'

function IndexPage({ data }) {
  const { t } = useTranslation()
  const heroSlides = Array.from({ length: 5 }, (_, index) => ({
    title: t(`index:hero.slides.${index}.title`),
    desc: t(`index:hero.slides.${index}.desc`),
    restrictions: t(`index:hero.restrictions`),
  }))
  const heroImages = [data.hero1, data.hero2, data.hero3, data.hero4, data.hero5]

  return (
    <Layout title={t('index:title')}>
      <HeroGallery
        slides={heroSlides.map((slide, index) => ({
          image: <Image fluid={heroImages[index].childImageSharp.fluid} layout="background" />,
          content: (
            <HeroBody marginX={0} alignSelf="flex-start" textAlign="left">
              <HeroTitle>{slide.title}</HeroTitle>
              {slide.desc && <HeroSubtitle>{slide.desc}</HeroSubtitle>}
              {slide.restrictions && (
                <HeroSubtitle fontSize={{ base: 'sm', md: 'md' }} marginTop={0}>
                  {slide.restrictions}
                </HeroSubtitle>
              )}
              <HeroCta>
                <ButtonBook size="lg" pointerEvents="all" />
              </HeroCta>
            </HeroBody>
          ),
        }))}
      />

      <Article>
        <Section spacing={10}>
          <RowContent>
            <ColVideo url="https://www.youtube.com/watch?v=ovk_nPBigSY" />
            <ColInfo title={t('index:intro.title')} backgroundColor="gray.100">
              <P>{t('index:intro.desc1')}</P>
              <P>{t('index:intro.desc2')}</P>
              <ButtonBook />
            </ColInfo>
          </RowContent>
          <Features />
        </Section>

        <Section title={t('suites:title')} subtitle={t('suites:desc')} titleAlign="center">
          <Rooms />
        </Section>

        <Section title={t('index:services.title')} titleAlign="center">
          <Services />
        </Section>

        <Section title={t('gallery:galleries.experience.title')} titleAlign="center">
          <Gallery
            images={data.gallery.edges.map((image) => (
              <Image key={v4()} fixed={image.node.childImageSharp.fixed} layout="background" />
            ))}
            buttonText={t('common:viewPhotos')}
          />
        </Section>

        <Section spacing={10}>
          <RowContent>
            <ColImage
              desc={t('index:events.restaurant.title')}
              image={<Image fixed={data.featured3.childImageSharp.fixed} layout="background" />}
            />
            <ColInfo title={t('index:events.restaurant.title')} backgroundColor="gray.100">
              <P>{t('index:events.restaurant.desc')}</P>
              <ButtonBook />
            </ColInfo>
          </RowContent>

          <RowContent reversedInMobile>
            <ColInfo title={t('index:events.spa.title')} backgroundColor="gray.100">
              <P>{t('index:events.spa.desc')}</P>
              <Button
                colorScheme="primary"
                variant="outline"
                as={Link}
                to="/spa"
                width="fit-content"
                _hover={{ backgroundColor: 'transparent' }}
              >
                {t('index:events.buttons.spa')}
              </Button>
            </ColInfo>
            <ColImage
              desc={t('index:events.spa.title')}
              image={<Image fixed={data.featured4.childImageSharp.fixed} layout="background" />}
            />
          </RowContent>

          <RowContent>
            <ColImage
              desc={t('index:events.event.title')}
              image={<Image fixed={data.featured1.childImageSharp.fixed} layout="background" />}
            />
            <ColInfo title={t('index:events.event.title')} backgroundColor="gray.100">
              <P>{t('index:events.event.desc')}</P>
              <Button
                colorScheme="primary"
                variant="outline"
                as={Link}
                to="/events"
                width="fit-content"
                _hover={{ backgroundColor: 'transparent' }}
              >
                {t('index:events.buttons.events')}
              </Button>
            </ColInfo>
          </RowContent>

          <RowContent reversedInMobile>
            <ColInfo title={t('index:events.weddings.title')} backgroundColor="gray.100">
              <P>{t('index:events.weddings.desc')}</P>
              <Button
                colorScheme="primary"
                variant="outline"
                as={Link}
                to="/weddings"
                width="fit-content"
                _hover={{ backgroundColor: 'transparent' }}
              >
                {t('index:events.buttons.weddings')}
              </Button>
            </ColInfo>
            <ColImage
              desc={t('index:events.weddings.title')}
              image={<Image fixed={data.featured2.childImageSharp.fixed} layout="background" />}
            />
          </RowContent>
        </Section>
      </Article>
    </Layout>
  )
}

export const query = graphql`
  query {
    hero1: file(relativePath: { eq: "index/hero1.jpeg" }) {
      ...bannerSectionImage
    }

    hero2: file(relativePath: { eq: "index/hero2.jpeg" }) {
      ...bannerSectionImage
    }

    hero3: file(relativePath: { eq: "index/hero3.jpeg" }) {
      ...bannerSectionImage
    }

    hero4: file(relativePath: { eq: "index/hero4.jpeg" }) {
      ...bannerSectionImage
    }

    hero5: file(relativePath: { eq: "index/hero5.jpeg" }) {
      ...bannerSectionImage
    }

    gallery: allFile(
      filter: { relativeDirectory: { eq: "index/gallery" } }
      sort: { fields: name }
    ) {
      ...multipleFixedImage
    }

    featured1: file(relativePath: { eq: "index/events.jpg" }) {
      ...fixedImage
    }

    featured2: file(relativePath: { eq: "index/weddings.jpg" }) {
      ...fixedImage
    }

    featured3: file(relativePath: { eq: "gallery/restaurant/Restaurante (2).jpg" }) {
      ...fixedImage
    }

    featured4: file(relativePath: { eq: "spa/featured.jpg" }) {
      ...fixedImage
    }

    featured5: file(
      relativePath: {
        eq: "tours/galleries/bacalar/57362489_604260966706568_6583381117473128448_n.jpg"
      }
    ) {
      ...fixedImage
    }

    house: file(relativePath: { eq: "index/hero3.jpeg" }) {
      ...fixedImage
    }

    vegan: file(relativePath: { eq: "index/hero3.jpeg" }) {
      ...fixedImage
    }

    temazcal: file(relativePath: { eq: "index/hero3.jpg" }) {
      ...fixedImage
    }

    spa: file(relativePath: { eq: "index/hero3.jpg" }) {
      ...fixedImage
    }
  }
`

export default IndexPage
